// Geboortevenster · Het paneel: vensterkeuze, marge-kaartjes, chart op de kandidaattijd, lanes, blok-popup en commit-balk.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { ChangeDetectionStrategy, Component, DestroyRef, Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ESegmentedToggleComponent, ESegmentedToggleOption } from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { FLOATING_PANEL_HEADER_ACTIONS, FloatingPanelHeaderAction } from '@shared/floating-panel/floating-panel-header-actions.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { FloatingPanelState } from '@shared/floating-panel/floating-panel.types';
import { SharedModule } from '@shared/shared.module';
import { HdBodygraphChartComponent } from '../bodygraph/hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HdBodygraphRenderStateFactory } from '../bodygraph/hd-bodygraph-render-state.factory';
import { HdBodygraphConfig } from '../bodygraph/hd-bodygraph.models';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdPlanetInfoService } from '../bodygraph/hd-planet-info.service';
import { getHdPlanetDisplayConfigByPlanetId } from '../bodygraph/planet-grid/hd-planet-display-config';
import { birthWindowChangesAt, buildBirthWindowIntervals } from './birth-window-activations.builder';
import { formatBirthClock, formatBirthWindowDuration, formatBirthWindowSignedDelta, sameBirthClockDay, hdBirthWindowTextKey } from './birth-window-format';
import { HdBirthWindowTierMargin, marginsFromLanes, planetTierMargin, tierMargins } from './birth-window-margins';
import { buildBirthWindowPlanetLanes, collectBirthWindowPlanetChanges } from './birth-window-planets.builder';
import { buildBirthWindowLanes, createBirthWindowEvaluator } from './birth-window-properties.builder';
import { HD_BIRTH_WINDOW_INDICATOR_TIERS, HD_BIRTH_WINDOW_TIERS, HdBirthWindowEvaluate, HdBirthWindowSegment, HdBirthWindowTier } from './birth-window.models';
import {
  HD_BIRTH_WINDOW_OVERLAY_TIERS,
  createHdBirthWindowOverlayEvaluator,
  filterHdBirthWindowOverlayLanes,
  toHdBirthWindowOverlayData,
} from './birth-window-moment';
import { HdBodygraphTransitOverlay } from '../bodygraph/hd-bodygraph-transit-overlay.models';
import { HdBirthWindowBlockPopupComponent, HdBirthWindowBlockPopupData } from './hd-birth-window-block-popup.component';
import { HdBirthWindowDataService } from './hd-birth-window-data.service';
import { HdBirthWindowLabelService } from './hd-birth-window-label.service';
import {
  HD_BIRTH_WINDOW_TIER_COLORS,
  HdBirthWindowBlockData,
  HdBirthWindowPanelVmContext,
  HdBirthWindowPlanetBlockData,
  buildBirthWindowLaneGroups,
} from './hd-birth-window-panel.vm';
import {
  HD_BIRTH_WINDOW_HALF_MINUTES,
  HdBirthWindowHalfMinutes,
  HdBirthWindowStateService,
} from './hd-birth-window-state.service';
import { HdPropertyLanesVerticalComponent } from './property-lanes/hd-property-lanes-vertical.component';
import { HdPropertyLanesComponent } from './property-lanes/hd-property-lanes.component';
import { HdPropertyLaneBlockClick } from './property-lanes/hd-property-lanes.models';

export const HD_BIRTH_WINDOW_PANEL_KEY = 'birth-window';
export const HD_BIRTH_WINDOW_ICON = 'hourglass-half';

const BW = 'humanDesign.chart.birthWindow';
const MINUTE_MS = 60_000;
/** Horizon van de schatting voor "eerste wijziging buiten het venster" en de marge-kaartjes. */
const ESTIMATE_HALF_WINDOW_MS = 48 * 3_600_000;
const EMPTY_CONFIG: HdBodygraphConfig = { schemaVersion: 1, activations: [] };

interface TierCardVm {
  readonly tier: HdBirthWindowTier;
  readonly label: string;
  readonly hint: string;
  readonly color: string;
  readonly colorLabel: string;
  readonly accent: string;
  readonly critical: boolean;
  readonly text: string;
  readonly drivers: string;
  /** Base: geen marge, telt niet mee (alleen als keuzetegel op phone). */
  readonly muted?: boolean;
}

/**
 * Paneel "Geboortevenster" (spec §3.2-3.3, feedbackronde 1 van 2026-10-16):
 * kop met vensterkeuze, drie marge-kaartjes, de chart op de kandidaattijd,
 * de lanes (Basics, Planeten, Variabelen, Base) met geboorte- en
 * kandidaatlijn, en de commit-balk. De pagina bezit chart en preview en
 * levert ze via `HdBirthWindowStateService`.
 *
 * Phone (sheet, Steven 2026-10-17): één laag tegelijk via keuzetegels, de
 * lanes verticaal (`hd-property-lanes-vertical`, scrollen = door de tijd), de
 * chart achter een oog-knop (scrolt mee) en een compacte sticky stapbalk met
 * korte knopteksten.
 */
@Component({
  selector: 'hd-birth-window-panel',
  imports: [SharedModule, ESegmentedToggleComponent, HdPropertyLanesComponent, HdPropertyLanesVerticalComponent, HdBodygraphChartComponent],
  templateUrl: './hd-birth-window-panel.component.html',
  styleUrl: './hd-birth-window-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.bw-panel--floating]': 'isFloatingPanel && !isSheet',
    '[class.bw-panel--narrow]': 'narrowActive()',
    '[class.bw-panel--sheet]': 'isSheet',
  },
})
export class HdBirthWindowPanelComponent {
  protected readonly state = inject(HdBirthWindowStateService);
  private readonly data = inject(HdBirthWindowDataService);
  private readonly labels = inject(HdBirthWindowLabelService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly destroyRef = inject(DestroyRef);
  private readonly headerActionsSignal = inject(FLOATING_PANEL_HEADER_ACTIONS, { optional: true });
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true }) as
    | { recenter(): void; readonly presentation?: string; readonly state?: Signal<FloatingPanelState> }
    | null;

  private readonly defaultEvaluate = createBirthWindowEvaluator(config => this.renderStateFactory.create(config));
  /** Bewaard moment: de natal chart als vaste laag bij elke proeftijd (`birth-window-moment.ts`). */
  private readonly evaluate = computed<HdBirthWindowEvaluate>(() => {
    const overlay = this.state.overlay();
    return overlay ? createHdBirthWindowOverlayEvaluator(overlay, config => this.renderStateFactory.create(config)) : this.defaultEvaluate;
  });
  /** Getoonde lagen: het momentvenster heeft geen Variabelen en Base. */
  private readonly tiers = computed(() => (this.state.overlay() ? HD_BIRTH_WINDOW_OVERLAY_TIERS : HD_BIRTH_WINDOW_TIERS));
  private windowRequestId = 0;
  private estimateRequestId = 0;

  protected readonly isFloatingPanel = this.headerActionsSignal != null;
  protected readonly isSheet = this.panelRef?.presentation === 'sheet';
  protected readonly narrowActive = computed(() => this.isFloatingPanel && !this.isSheet && this.state.panelNarrow());

  protected readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly windowOptions = computed<ESegmentedToggleOption<string>[]>(() => {
    const lang = this.lang();
    return HD_BIRTH_WINDOW_HALF_MINUTES.map(minutes => ({ value: String(minutes), label: this.t(`windows.${minutes}`, lang) }));
  });

  protected readonly windowValue = computed(() => String(this.state.halfWindowMinutes()));

  protected readonly available = computed(() => this.state.preset() != null && this.state.birthMs() !== undefined);

  protected readonly bornText = computed(() => {
    const birthMs = this.state.birthMs();
    if (birthMs === undefined) return '';
    const lang = this.lang();
    return this.t('bornAt', lang, { time: formatBirthClock(birthMs, this.state.timeZoneId(), lang, { date: true }) });
  });

  protected readonly birthClockText = computed(() => this.t('birthClock', this.lang(), { timezone: this.state.timeZoneId() ?? 'UTC' }));

  protected readonly intervals = computed(() => {
    const data = this.state.data();
    return data ? buildBirthWindowIntervals(data) : [];
  });

  protected readonly lanes = computed(() => {
    const data = this.state.data();
    if (!data) return [];
    const lanes = buildBirthWindowLanes(this.intervals(), data.birthMs, this.evaluate());
    return this.state.overlay() ? filterHdBirthWindowOverlayLanes(lanes) : lanes;
  });

  private readonly planetLanes = computed(() => {
    const data = this.state.data();
    return data ? buildBirthWindowPlanetLanes(data, data.birthMs) : [];
  });

  private readonly planetChanges = computed(() => {
    const data = this.state.data();
    return data ? collectBirthWindowPlanetChanges(data) : [];
  });

  private readonly estimateMargins = computed(() => {
    const estimate = this.state.estimate();
    if (!estimate) return undefined;
    const all = buildBirthWindowLanes(buildBirthWindowIntervals(estimate), estimate.birthMs, this.evaluate());
    const lanes = this.state.overlay() ? filterHdBirthWindowOverlayLanes(all) : all;
    return marginsFromLanes(lanes, estimate.birthMs, true);
  });

  private readonly exactTiers = computed<readonly HdBirthWindowTierMargin[] | undefined>(() => {
    const data = this.state.data();
    if (!data) return undefined;
    return tierMargins(marginsFromLanes(this.lanes(), data.birthMs), planetTierMargin(data, data.birthMs));
  });

  private readonly estimateTiers = computed(() => {
    const margins = this.estimateMargins();
    const estimate = this.state.estimate();
    if (!margins || !estimate) return undefined;
    return tierMargins(margins, planetTierMargin(estimate, estimate.birthMs, true));
  });

  private readonly vmContext = computed<HdBirthWindowPanelVmContext | undefined>(() => {
    const birthMs = this.state.birthMs();
    if (birthMs === undefined) return undefined;
    const lang = this.lang();
    return {
      lang,
      timeZone: this.state.timeZoneId(),
      birthMs,
      labels: this.labels,
      t: (key, params) => this.t(key, lang, params),
      overlay: this.state.overlay() != null,
    };
  });

  protected readonly groups = computed(() => {
    const ctx = this.vmContext();
    if (!ctx || this.lanes().length === 0) return [];
    return buildBirthWindowLaneGroups({
      lanes: this.lanes(),
      estimateMargins: this.estimateMargins(),
      planetLanes: this.planetLanes(),
      planetChanges: this.planetChanges(),
      planetsExpanded: this.state.planetsExpanded(),
      tiers: this.tiers(),
    }, ctx);
  });

  protected readonly tierCards = computed<TierCardVm[]>(() => {
    const ctx = this.vmContext();
    if (!ctx) return [];
    const exact = this.exactTiers();
    const estimate = this.estimateTiers();
    return HD_BIRTH_WINDOW_INDICATOR_TIERS.filter(tier => this.tiers().includes(tier)).map(tier => {
      const exactMargin = exact?.find(candidate => candidate.tier === tier);
      const estimateMargin = estimate?.find(candidate => candidate.tier === tier);
      // Exact binnen het venster; buiten het venster valt de kaart terug op de schatting (met ≈).
      const margin = exactMargin?.nearestMs !== undefined ? exactMargin : estimateMargin ?? exactMargin;
      const label = this.labels.tierLabel(tier, ctx.lang);
      const color = margin?.color ?? 'green';
      return {
        tier,
        label,
        hint: this.labels.tierHint(tier, ctx.lang, ctx.overlay),
        color,
        colorLabel: this.t(`colors.${color}`, ctx.lang),
        accent: HD_BIRTH_WINDOW_TIER_COLORS[tier],
        critical: margin?.critical ?? false,
        text: margin ? this.marginText(margin, ctx) : '',
        drivers: margin?.drivers.map(key => this.labels.propertyLabel(key, ctx.lang)).join(', ') ?? '',
      };
    });
  });

  // ── Phone ───────────────────────────────────────────────────────────────────
  /** Gekozen laag op phone (één tegelijk). */
  protected readonly phoneTier = signal<HdBirthWindowTier>('basics');
  /** Chart op phone standaard dicht: ze kost een half scherm. */
  protected readonly phoneChartVisible = signal(false);

  /** Vier keuzetegels: de drie marge-kaartjes plus Base (gedempt, zonder marge). */
  protected readonly phoneTiles = computed<TierCardVm[]>(() => {
    const ctx = this.vmContext();
    if (!ctx) return [];
    const cards = this.tierCards();
    return this.tiers().map<TierCardVm>(tier => cards.find(card => card.tier === tier) ?? {
      tier,
      label: this.labels.tierLabel(tier, ctx.lang),
      hint: this.labels.tierHint(tier, ctx.lang, ctx.overlay),
      color: 'muted',
      colorLabel: '',
      accent: HD_BIRTH_WINDOW_TIER_COLORS[tier],
      critical: false,
      text: this.labels.tierHint(tier, ctx.lang, ctx.overlay),
      drivers: '',
      muted: true,
    });
  });

  /** Enkel de gekozen groep; planeten altijd als wissellog (26 sporen passen niet op 375 px). */
  protected readonly phoneGroups = computed(() =>
    this.groups()
      .filter(group => group.key === this.phoneTier())
      .map(group => (group.key === 'planets' ? { ...group, collapsed: true, collapsible: false } : group)));

  protected selectPhoneTier(tier: HdBirthWindowTier): void {
    this.phoneTier.set(tier);
  }

  protected togglePhoneChart(): void {
    this.phoneChartVisible.update(visible => !visible);
  }

  protected tileSelectLabel(tile: TierCardVm): string {
    return this.t('tiles.select', this.lang(), { tier: tile.label });
  }

  protected readonly anchorLabel = computed(() => {
    const birthMs = this.state.birthMs();
    return birthMs === undefined ? '' : formatBirthClock(birthMs, this.state.timeZoneId(), this.lang());
  });

  protected readonly markerLabel = computed(() => {
    const candidate = this.state.candidateMs();
    const birthMs = this.state.birthMs();
    if (candidate === undefined || birthMs === undefined) return '';
    return formatBirthWindowSignedDelta(candidate - birthMs, this.lang());
  });

  protected readonly candidateText = computed(() => {
    const candidate = this.state.candidateMs();
    const birthMs = this.state.birthMs();
    if (candidate === undefined || birthMs === undefined) return '';
    const lang = this.lang();
    const withDate = !sameBirthClockDay(candidate, birthMs, this.state.timeZoneId());
    return `${formatBirthClock(candidate, this.state.timeZoneId(), lang, { seconds: true, date: withDate })} · ${this.t('commit.delta', lang, {
      delta: formatBirthWindowSignedDelta(candidate - birthMs, lang),
    })}`;
  });

  /**
   * Bewaard moment: de natal chart met de transitplaneten van de proeftijd (of
   * van het moment zelf) als transitlaag, zoals de chart op de pagina.
   */
  protected readonly chartTransitOverlay = computed<HdBodygraphTransitOverlay | null>(() => {
    const overlay = this.state.overlay();
    if (!overlay) return null;
    const activations = (this.state.previewPreset() ?? this.state.preset())?.planetaryActivations ?? [];
    return { activations, colorMode: overlay.colorMode };
  });

  /** Chart in het paneel: de preview op de kandidaattijd, anders de geboortechart. */
  protected readonly chartConfiguration = computed<HdBodygraphConfig>(() =>
    this.state.previewPreset()?.configuration ?? this.state.preset()?.configuration ?? EMPTY_CONFIG,
  );

  protected readonly chartCaption = computed(() => {
    const lang = this.lang();
    const candidate = this.state.candidateMs();
    const birthMs = this.state.birthMs();
    if (candidate === undefined || birthMs === undefined) return this.t('chart.atBirth', lang);
    const withDate = !sameBirthClockDay(candidate, birthMs, this.state.timeZoneId());
    return this.t('chart.atCandidate', lang, {
      time: formatBirthClock(candidate, this.state.timeZoneId(), lang, { date: withDate }),
      delta: formatBirthWindowSignedDelta(candidate - birthMs, lang),
    });
  });

  /** De preview uit de segmenten draagt een markering; het exacte document niet. */
  protected readonly chartExactPending = computed(() => {
    const preview = this.state.previewPreset();
    return preview != null && (preview.metadata as { birthWindowPreview?: boolean } | undefined)?.birthWindowPreview === true;
  });

  protected readonly canInteract = computed(() => this.state.requestHandlers() != null && this.available());

  /** Alle wisselmomenten (voor de vorige/volgende-knoppen), base-wissels inbegrepen. */
  private readonly boundaries = computed(() => {
    const data = this.state.data();
    if (!data) return [];
    const set = new Set<number>();
    for (const body of data.bodies) {
      for (let i = 1; i < body.segments.length; i++) set.add(body.segments[i].fromMs);
    }
    return [...set].sort((a, b) => a - b);
  });

  protected readonly hintText = computed(() => this.t(this.isSheet ? 'hintPhone' : 'hint', this.lang()));
  protected readonly loadingText = computed(() => this.t('loading', this.lang()));
  protected readonly errorText = computed(() => this.state.error() ?? this.t('error', this.lang()));
  protected readonly labelsVm = computed(() => {
    const lang = this.lang();
    return {
      title: this.t('title', lang),
      subtitle: this.t('subtitle', lang),
      windowGroup: this.t('windowGroupLabel', lang),
      retry: this.t('retry', lang),
      unavailable: this.t('unavailable', lang),
      candidate: this.t('commit.candidate', lang),
      // Phone: korte knopteksten zodat de sticky stapbalk één regel per knop houdt.
      apply: this.t(this.isSheet ? 'commit.applyShort' : 'commit.apply', lang),
      reset: this.t(this.isSheet ? 'commit.resetShort' : 'commit.reset', lang),
      legendBirth: this.t('legend.birth', lang),
      legendSame: this.t('legend.same', lang),
      legendDifferent: this.t('legend.different', lang),
      legendCandidate: this.t('legend.candidate', lang),
      markerHandle: this.t('markerHandle', lang),
      ariaLanes: this.t('ariaLanes', lang),
      critical: this.t('critical', lang),
      chartShow: this.t('chart.show', lang),
      chartHide: this.t('chart.hide', lang),
      chartExactPending: this.t('chart.exactPending', lang),
    };
  });

  constructor() {
    // Exact venster laden bij een andere chart of breedte.
    effect(() => {
      const preset = this.state.preset();
      const minutes = this.state.halfWindowMinutes();
      const birthMs = this.state.birthMs();
      untracked(() => void this.loadWindow(preset, minutes, birthMs));
    });
    // Schatting (ruime horizon) per chart.
    effect(() => {
      const preset = this.state.preset();
      untracked(() => void this.loadEstimate(preset));
    });
    // Zwevend: verklein-/vergroot-knop in de paneelkop (zelfde knop en teksten als het transits-paneel).
    effect(() => {
      if (!this.headerActionsSignal || this.isSheet) return;
      const lang = this.lang();
      const narrow = this.state.panelNarrow();
      const action: FloatingPanelHeaderAction = {
        icon: narrow ? 'up-right-and-down-left-from-center' : 'down-left-and-up-right-to-center',
        tooltip: this.transloco.translate(
          narrow ? 'humanDesign.chart.transits.shortcutBar.widenTooltip' : 'humanDesign.chart.transits.shortcutBar.shrinkTooltip',
          undefined,
          lang,
        ),
        onClick: () => this.toggleNarrow(),
      };
      this.headerActionsSignal.set([action]);
    });
    this.destroyRef.onDestroy(() => {
      this.windowRequestId++;
      this.estimateRequestId++;
    });
  }

  private async loadWindow(preset: ReturnType<HdBirthWindowStateService['preset']>, minutes: number, birthMs: number | undefined): Promise<void> {
    const id = ++this.windowRequestId;
    if (!preset || birthMs === undefined) {
      this.state.data.set(undefined);
      this.state.loading.set(false);
      return;
    }
    this.state.loading.set(true);
    this.state.error.set(undefined);
    try {
      const data = await this.data.getWindow(preset, minutes);
      if (id !== this.windowRequestId) return;
      this.state.data.set(this.state.overlay() ? toHdBirthWindowOverlayData(data) : data);
    } catch (error) {
      if (id !== this.windowRequestId) return;
      this.state.data.set(undefined);
      this.state.error.set(error instanceof Error && error.message ? error.message : undefined);
    } finally {
      if (id === this.windowRequestId) this.state.loading.set(false);
    }
  }

  private async loadEstimate(preset: ReturnType<HdBirthWindowStateService['preset']>): Promise<void> {
    const id = ++this.estimateRequestId;
    if (!preset) {
      this.state.estimate.set(undefined);
      return;
    }
    try {
      const estimate = await this.data.getEstimate(preset, ESTIMATE_HALF_WINDOW_MS);
      if (id === this.estimateRequestId) {
        this.state.estimate.set(estimate && this.state.overlay() ? toHdBirthWindowOverlayData(estimate) : estimate);
      }
    } catch {
      if (id === this.estimateRequestId) this.state.estimate.set(undefined);
    }
  }

  protected retry(): void {
    this.data.clear();
    void this.loadWindow(this.state.preset(), this.state.halfWindowMinutes(), this.state.birthMs());
  }

  protected setWindow(value: string): void {
    const minutes = Number(value) as HdBirthWindowHalfMinutes;
    if (!HD_BIRTH_WINDOW_HALF_MINUTES.includes(minutes)) return;
    this.state.halfWindowMinutes.set(minutes);
  }

  protected onGroupToggled(groupKey: string): void {
    if (groupKey === 'planets') this.state.planetsExpanded.update(expanded => !expanded);
  }

  protected toggleChart(): void {
    this.state.chartVisible.update(visible => !visible);
  }

  protected onMarkerChanged(ms: number): void {
    this.setCandidate(ms);
  }

  protected onMarkerCommitted(ms: number): void {
    this.setCandidate(ms);
  }

  protected onEmptyClicked(ms: number): void {
    if (!this.canInteract()) return;
    this.setCandidate(ms);
  }

  protected stepCandidate(deltaMs: number): void {
    const birthMs = this.state.birthMs();
    if (birthMs === undefined) return;
    const current = this.state.candidateMs() ?? birthMs;
    this.setCandidate(current + deltaMs);
  }

  protected jumpToChange(direction: -1 | 1): void {
    const birthMs = this.state.birthMs();
    if (birthMs === undefined) return;
    const current = this.state.candidateMs() ?? birthMs;
    const boundaries = this.boundaries();
    const next = direction > 0
      ? boundaries.find(boundary => boundary > current + 500)
      : [...boundaries].reverse().find(boundary => boundary < current - 500);
    if (next !== undefined) this.setCandidate(next);
  }

  protected resetCandidate(): void {
    this.state.candidateMs.set(undefined);
    this.state.requestHandlers()?.previewAt(undefined);
  }

  protected async commit(): Promise<void> {
    const candidate = this.state.candidateMs();
    const handlers = this.state.requestHandlers();
    if (candidate === undefined || !handlers) return;
    await handlers.commit(candidate);
  }

  private setCandidate(ms: number): void {
    if (!this.canInteract()) return;
    const data = this.state.data();
    const birthMs = this.state.birthMs();
    if (birthMs === undefined) return;
    const from = data?.fromMs ?? birthMs - this.state.halfWindowMinutes() * MINUTE_MS;
    const to = data?.toMs ?? birthMs + this.state.halfWindowMinutes() * MINUTE_MS;
    const clamped = Math.min(Math.max(Math.round(ms / 1000) * 1000, from), to);
    if (Math.abs(clamped - birthMs) < 500) {
      this.resetCandidate();
      return;
    }
    this.state.candidateMs.set(clamped);
    this.state.requestHandlers()?.previewAt(clamped);
  }

  protected onBlockClicked(click: HdPropertyLaneBlockClick): void {
    const ctx = this.vmContext();
    const data = this.state.data();
    const blockData = click.block.data as HdBirthWindowBlockData | HdBirthWindowPlanetBlockData | undefined;
    if (!ctx || !data || !blockData) return;
    const lang = ctx.lang;
    const zone = ctx.timeZone;
    const fromMs = blockData.kind === 'planet' ? blockData.fromMs : blockData.block.fromMs;
    const toMs = blockData.kind === 'planet' ? blockData.toMs : blockData.block.toMs;
    const state = blockData.kind === 'planet' ? blockData.state : blockData.block.state;
    const withDate = !sameBirthClockDay(fromMs, ctx.birthMs, zone) || !sameBirthClockDay(toMs, ctx.birthMs, zone);
    const causes = fromMs > data.fromMs ? birthWindowChangesAt(data, fromMs) : [];
    const propertyLabel = blockData.kind === 'planet'
      ? `${this.labels.planetLabel(blockData.sourceKey, lang)} (${this.t(`planets.${blockData.side}Track`, lang)})`
      : this.labels.propertyLabel(blockData.lane.key, lang);
    const valueLabel = blockData.kind === 'planet'
      ? `${blockData.gate}.${blockData.line}`
      : this.labels.valueLabelFull(blockData.block.value.payload, lang);
    const popupData: HdBirthWindowBlockPopupData = {
      propertyLabel,
      valueLabel,
      stateLabel: this.t(`legend.${state}`, lang),
      state,
      fromText: formatBirthClock(fromMs, zone, lang, { seconds: true, date: withDate }),
      toText: formatBirthClock(toMs, zone, lang, { seconds: true, date: withDate }),
      durationText: formatBirthWindowDuration(toMs - fromMs, lang),
      utcText: `UTC ${formatBirthClock(fromMs, 'UTC', lang, { seconds: true, date: true })} – ${formatBirthClock(toMs, 'UTC', lang, { seconds: true, date: true })}`,
      causesLabel: this.t('popup.causes', lang),
      causes: causes.map(cause => {
        const display = getHdPlanetDisplayConfigByPlanetId(cause.planetId);
        const name = display ? this.planetInfo.planetInfoIn(display.key, lang)?.name ?? display.label : cause.sourceKey;
        return {
          glyph: display?.astronomiconChar ?? '',
          planet: name,
          side: this.t(`popup.side.${cause.side}`, lang),
          from: formatSegmentCell(cause.from),
          to: formatSegmentCell(cause.to),
        };
      }),
      noCauseText: this.t('popup.noCause', lang),
      setTimeLabel: this.t('popup.setTimeHere', lang),
      setTimeMs: this.canInteract() ? (state === 'birth' ? ctx.birthMs : fromMs) : undefined,
      fromLabel: this.t('popup.from', lang),
      toLabel: this.t('popup.to', lang),
      durationLabel: this.t('popup.duration', lang),
    };
    this.floatingPanels.openOrFocus<HdBirthWindowBlockPopupComponent, HdBirthWindowBlockPopupData>({
      key: `birth-window-block:${click.laneKey}:${fromMs}`,
      component: HdBirthWindowBlockPopupComponent,
      data: popupData,
      dockTab: { label: popupData.propertyLabel },
      bypassAutoDock: true,
      startPosition: { preferred: click.origin, hint: 'prefer-right-of-anchor' },
    });
  }

  private toggleNarrow(): void {
    const willWiden = this.state.panelNarrow();
    this.state.panelNarrow.update(narrow => !narrow);
    if (willWiden) this.panelRef?.recenter();
  }

  private marginText(margin: HdBirthWindowTierMargin, ctx: HdBirthWindowPanelVmContext): string {
    const lang = ctx.lang;
    if (margin.nearestMs === undefined) {
      return this.t('margin.noChange', lang, { hours: ESTIMATE_HALF_WINDOW_MS / 3_600_000 });
    }
    const at = margin.direction === 'before' ? ctx.birthMs - margin.nearestMs : ctx.birthMs + margin.nearestMs;
    const withDate = !sameBirthClockDay(at, ctx.birthMs, ctx.timeZone);
    const time = formatBirthClock(at, ctx.timeZone, lang, { date: withDate });
    const delta = this.t(margin.direction === 'before' ? 'margin.earlier' : 'margin.later', lang, {
      delta: formatBirthWindowDuration(margin.nearestMs, lang),
    });
    const key = margin.direction === 'after' ? 'margin.stableUntil' : 'margin.changesAt';
    const text = this.t(key, lang, { time, delta });
    return margin.estimated ? `≈ ${text}` : text;
  }

  private t(key: string, lang: string, params?: Record<string, unknown>): string {
    return this.transloco.translate<string>(`${BW}.${hdBirthWindowTextKey(key, this.state.momentMode())}`, params, lang);
  }
}

function formatSegmentCell(segment: HdBirthWindowSegment): string {
  const base = `${segment.gate}.${segment.line}`;
  if (segment.color === undefined || segment.tone === undefined) return base;
  const colorTone = `${base}.${segment.color}.${segment.tone}`;
  return segment.base !== undefined ? `${colorTone}.${segment.base}` : colorTone;
}
